import { LinkIcon, X } from "lucide-react";

/**
 * What a shared link couldn't do, said out loud.
 *
 * Both cases look identical from the outside — you followed a link and the map
 * isn't showing you what you were sent — so both need naming. "Missing" is a
 * unit that has since been taken down; "filtered" is one the recipient's own
 * filters exclude, which is the more common and the more fixable of the two,
 * and so is the one that gets a button.
 */
export type SharedLinkMiss = "missing" | "filtered";

export function SharedLinkNotice({
  miss,
  onShowAnyway,
  onDismiss,
}: {
  miss: SharedLinkMiss;
  onShowAnyway: () => void;
  onDismiss: () => void;
}) {
  return (
    // Centred with `inset-x` + `mx-auto`, not `left-1/2` + `-translate-x-1/2`:
    // an absolutely positioned box only gets the space from its `left` edge
    // onward, so anchoring at 50% caps the width at half the screen and wraps
    // this to one word per line on a phone.
    //
    // Cleared below the mobile identity bar, which is the same offset the scale
    // bar uses. On desktop the console sits far to the left of centre, so the
    // one position works for both.
    <div
      className="console pointer-events-auto absolute inset-x-3 z-50 mx-auto flex w-fit max-w-[30rem] items-center gap-2.5 rounded-console px-3 py-2 text-[12px] text-chalk"
      style={{ top: "calc(max(0.5rem, env(safe-area-inset-top)) + 3.75rem)" }}
    >
      <LinkIcon className="size-3.5 shrink-0 text-signal" />
      {miss === "missing" ? (
        <span className="min-w-0">
          That listing is no longer active —{" "}
          <span className="text-chalk-dim">it was taken down since the link was made.</span>
        </span>
      ) : (
        <>
          <span className="min-w-0">
            Shared listing is <span className="text-chalk-dim">hidden by your filters.</span>
          </span>
          <button
            type="button"
            onClick={onShowAnyway}
            className="shrink-0 rounded border border-rule px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.1em] text-chalk-dim transition hover:border-rule-bright hover:text-chalk"
          >
            Show it
          </button>
        </>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 shrink-0 rounded p-1 text-chalk-mute transition hover:text-chalk"
      >
        <X className="size-3.5" />
      </button>
    </div>
  );
}
